import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import GeneralTablesCellsTdChange from 'lumen/components/general/tables/cells/td-change';
import HotelAndMarketRevenueInsightTablesCellsTdTitleAttribute from 'lumen/components/hotel-and-market/revenue-insight/tables/cells/td-title-attribute';
import { KPIS } from 'lumen/utils/constants/ri-constant-values';
import roundSegmentationValue from 'lumen/utils/revenue-insight/round-segmentation-value';
import {
  compareLeadTime,
  compareLOSBucket,
  compareNameAsNumber,
  compareNumbers,
  comparePickupCount,
  compareStrings,
  compareWeekday,
} from 'lumen/utils/revenue-insight/sort-functions';
import {
  changeValueFormat,
  valueFormat,
} from 'lumen/utils/revenue-insight/value-formatting';

const COLUMN_PROPERTIES = {
  NAME: 'segmentationValue',
  CURRENT: 'current',
  REFERENCE: 'reference',
  CHANGE: 'change',
  BUDGET_FORECAST_CURRENT: 'budgetForecastCurrent',
  BUDGET_CHANGE: 'budgetForecastChange',
  FORECAST_CHANGE: 'forecastChange',
  PICKUP: 'pickup',
};

interface HotelAndMarketRevenueInsightTablesTableBreakdownDetailSignature {
  Args: {
    allModifiedSegmentationObjects: unknown;
    blockName: unknown;
    visibleKpis: unknown;
  };
}

export default class HotelAndMarketRevenueInsightTablesTableBreakdownDetail extends Component<HotelAndMarketRevenueInsightTablesTableBreakdownDetailSignature> {
  @service declare intl: Services['intl'];

  @service declare riSelections: Services['ri-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const columns = [
      {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        name: this.args.blockDisplayName,
        valuePath: COLUMN_PROPERTIES.NAME,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.NAME}`,
        textAlign: 'left',
        hasRightBorder: true,
        tdComponent: HotelAndMarketRevenueInsightTablesCellsTdTitleAttribute,
        ...(this.args.blockName === RI_BREAKDOWNS.SEGMENT
          ? {
              minWidth: 140,
              width: 140,
              maxWidth: 140,
            }
          : {}),
      },
    ];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.visibleKpis.forEach((kpi) => {
      columns.push({
        name: this.intl.t(`revenue_insight.${kpi}`),
        // @ts-expect-error: Incorrect type
        subcolumns: this.subColumns.map((subColumn, i) => {
          return {
            name: subColumn.name,
            valuePath: `${subColumn.valuePath}-${kpi}`,
            displayValuePath: `displayValue.${subColumn.valuePath}-${kpi}`,
            tdComponent: subColumn.tdComponent,
            textAlign: 'center',
            invertChangeColor: this.riSelections.isCancellationsOnlyView,
            hasRightBorder: i === this.subColumns.length - 1,
          };
        }),
      });
    });

    return columns;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get initialSorts() {
    return [
      {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        valuePath: this.columns[1].subcolumns[0].valuePath,
        isAscending: false,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.getRowDataForSegmentationObjects(
      this.args.allModifiedSegmentationObjects,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subColumns() {
    const isBlockPickup = this.args.blockName === 'pickup';

    if (this.riSelections.isTargetActive && !isBlockPickup) {
      const showPickupLYColumn =
        this.riSelections.showPickupLY && this.riSelections.isToDateInFuture;

      const subColumns = [
        {
          name: this.riSelections.currentPeriodText,
          valuePath: COLUMN_PROPERTIES.BUDGET_FORECAST_CURRENT,
        },
      ];

      if (this.riSelections.isBudgetActive) {
        subColumns.push({
          name: this.intl.t('revenue_insight.diff_budget.short'),
          valuePath: COLUMN_PROPERTIES.BUDGET_CHANGE,
          // @ts-expect-error: Incorrect type
          tdComponent: GeneralTablesCellsTdChange,
        });
      } else {
        subColumns.push({
          name: this.intl.t('revenue_insight.diff_forecast.short'),
          valuePath: COLUMN_PROPERTIES.FORECAST_CHANGE,
          // @ts-expect-error: Incorrect type
          tdComponent: GeneralTablesCellsTdChange,
        });
      }

      if (showPickupLYColumn) {
        subColumns.push({
          name: this.riSelections.leftToPickupText,
          valuePath: COLUMN_PROPERTIES.PICKUP,
        });
      }

      return subColumns;
    }

    return [
      {
        name: this.riSelections.currentPeriodText,
        valuePath: COLUMN_PROPERTIES.CURRENT,
      },
      {
        name: this.riSelections.referencePeriodText,
        valuePath: COLUMN_PROPERTIES.REFERENCE,
      },
      {
        name: this.intl.t('revenue_insight.expand_view.change'),
        valuePath: COLUMN_PROPERTIES.CHANGE,
        tdComponent: GeneralTablesCellsTdChange,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subColumnsValuePaths() {
    return this.subColumns.map((subColumn) => subColumn.valuePath);
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRowDataForSegmentationObjects(segmentationObjects) {
    // @ts-expect-error: Incorrect type
    const mappedSegmentations = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    segmentationObjects.forEach((segmentation) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const roundedData = roundSegmentationValue(segmentation, true);

      const mappedSegmentation = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        segmentation,

        // We want the rows to be collapsed by default
        isCollapsed: true,

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        [COLUMN_PROPERTIES.NAME]: segmentation.segmentationValue,

        displayValue: {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          [COLUMN_PROPERTIES.NAME]: segmentation.displayValue,
        },
      };

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.visibleKpis.forEach((kpi) => {
        const currentKey = `${COLUMN_PROPERTIES.CURRENT}-${kpi}`;
        const referenceKey = `${COLUMN_PROPERTIES.REFERENCE}-${kpi}`;
        const changeKey = `${COLUMN_PROPERTIES.CHANGE}-${kpi}`;
        const budgetForecastCurrentKey = `${COLUMN_PROPERTIES.BUDGET_FORECAST_CURRENT}-${kpi}`;
        const budgetChangeKey = `${COLUMN_PROPERTIES.BUDGET_CHANGE}-${kpi}`;
        const forecastChangeKey = `${COLUMN_PROPERTIES.FORECAST_CHANGE}-${kpi}`;
        const pickupKey = `${COLUMN_PROPERTIES.PICKUP}-${kpi}`;

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const currentValue = roundedData[this.riSelections.bobKey][kpi];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const referenceValue = roundedData[this.riSelections.referenceKey][kpi];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const changeValue = roundedData[this.riSelections.changeKey][kpi];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const budgetForecastCurrent = roundedData.BOB[kpi];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const budgetChange = roundedData.budgetChange[kpi];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const forecastChange = roundedData.forecastChange[kpi];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const pickupLastYear = roundedData.pickupLastYear[kpi];

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        mappedSegmentation[currentKey] = currentValue;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        mappedSegmentation[referenceKey] = referenceValue;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        mappedSegmentation[changeKey] = changeValue;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        mappedSegmentation[budgetForecastCurrentKey] = budgetForecastCurrent;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        mappedSegmentation[budgetChangeKey] = budgetChange;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        mappedSegmentation[forecastChangeKey] = forecastChange;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        mappedSegmentation[pickupKey] = pickupLastYear;

        const formattedCurrent = this.formatValue(currentValue, kpi);
        const formattedReference = this.formatValue(referenceValue, kpi);
        const formattedChange = this.formatChange(changeValue, kpi);
        const formattedBudgetForecastCurrent = this.formatValue(
          budgetForecastCurrent,
          kpi,
        );
        const formattedBudgetChange = this.formatChange(budgetChange, kpi);
        const formattedForecastChange = this.formatChange(forecastChange, kpi);
        const formattedPickup = this.formatPickup(pickupLastYear, kpi);

        mappedSegmentation.displayValue[currentKey] = formattedCurrent;
        mappedSegmentation.displayValue[referenceKey] = formattedReference;
        mappedSegmentation.displayValue[changeKey] = formattedChange;
        mappedSegmentation.displayValue[budgetForecastCurrentKey] =
          formattedBudgetForecastCurrent;
        mappedSegmentation.displayValue[budgetChangeKey] =
          formattedBudgetChange;
        mappedSegmentation.displayValue[forecastChangeKey] =
          formattedForecastChange;
        mappedSegmentation.displayValue[pickupKey] = formattedPickup;
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (segmentation.children) {
        const mappedChildren = this.getRowDataForSegmentationObjects(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          segmentation.children,
        );
        mappedSegmentation.children = mappedChildren;
      }

      mappedSegmentations.push(mappedSegmentation);
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return mappedSegmentations;
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatChange(value, kpi) {
    const showPercentages =
      this.riSelections.isCancellationsPercentageView && kpi !== KPIS.OCCUPANCY;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return changeValueFormat(value, showPercentages, this.intl);
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatPickup(value, kpi) {
    if (value === 0 || value === null) {
      return this.intl.t('general.not_available_short');
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const signDisplay = kpi.indexOf('adr') !== -1 ? 'auto' : 'always';

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return this.intl.formatNumber(value, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      signDisplay,
    });
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatValue(value, kpi) {
    const showPercentages =
      this.riSelections.isCancellationsPercentageView && kpi !== KPIS.OCCUPANCY;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return valueFormat(value, showPercentages, this.intl);
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sortFunction(rowA, rowB, sorts) {
    let result = 0;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    sorts.some((sort) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { valuePath, isAscending } = sort;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const valueA = rowA[valuePath];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const valueB = rowB[valuePath];

      if (valuePath === COLUMN_PROPERTIES.NAME) {
        result = this.sortNameColumn(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rowA.segmentation,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rowB.segmentation,
          isAscending,
        );
        return result !== 0;
      }

      if (valueA === null) {
        result = 1;
        return true;
      }

      if (valueB === null) {
        result = -1;
        return true;
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        result = compareNumbers(valueA, valueB, isAscending);
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        result = compareStrings(valueA, valueB, isAscending);
      }

      return result !== 0;
    });

    return result;
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sortNameColumn(segmentationA, segmentationB, isAscending) {
    const { blockName } = this.args;

    if (blockName === RI_BREAKDOWNS.LOS) {
      if (this.riSelections.isMultiProperty) {
        return compareLOSBucket(segmentationA, segmentationB, isAscending);
      }
      return compareNameAsNumber(segmentationA, segmentationB, isAscending);
    }
    if (blockName === RI_BREAKDOWNS.PICKUP) {
      return comparePickupCount(segmentationA, segmentationB, isAscending);
    }
    if (blockName === RI_BREAKDOWNS.WEEKDAY) {
      return compareWeekday(segmentationA, segmentationB, isAscending);
    }
    if (blockName === RI_BREAKDOWNS.BOOKING_LEAD_TIME) {
      return compareLeadTime(segmentationA, segmentationB, isAscending);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const valueA = segmentationA[COLUMN_PROPERTIES.NAME];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const valueB = segmentationB[COLUMN_PROPERTIES.NAME];
    return compareStrings(valueA, valueB, isAscending);
  }

  <template>
    <PrismTable
      {{! @glint-expect-error: Incorrect type }}
      @columns={{this.columns}}
      @enableAutoSortsUpdate={{true}}
      @initialSorts={{this.initialSorts}}
      @removeTableBorder={{true}}
      @rows={{this.rows}}
      @sortFunction={{this.sortFunction}}
    />
  </template>
}
